import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { Envelope, GameType, PlayerAchievementBadge } from '../types';

// ── Player profile ───────────────────────────────────────────────────

export interface PlayerStats {
  totalGames: number;
  gamesWon: number;
  /** Every earned achievement row, hidden lower tiers included (legacy count). */
  achievements: number;
  playTime: number;
  playTimeHuman: string;
}

export interface LatestPointsEntry {
  points: number;
  /** The points were the game's winner's ("as winner"). */
  isWinner: boolean;
  context: string;
  /** The game's goal, preformatted ("2000 pop"); null when the game is gone. */
  goal: string | null;
  gameId: number;
  /** Humanised server-side ("2 hours ago"). */
  when: string;
  /** The moment behind `when`, for its tooltip; null when unknown. */
  at: string | null;
}

export interface SeasonResultEntry {
  num: number;
  rank: number;
  games: number;
  points: number;
}

/**
 * A player's fastest win on one visible server, against that server's record.
 * One entry per server (`serverId` is unique within `PlayerProfile.personalBests`).
 */
export interface PersonalBestEntry {
  serverId: number;
  server: string;
  type: GameType;
  gameId: number;
  /** Raw winner time in seconds behind `time` (for sorting / comparison). */
  seconds: number;
  /** Preformatted `Hh Mm Ss` / `Mm Ss`. */
  time: string;
  /** Humanised server-side ("2 hours ago"); null when the end time is unknown. */
  when: string | null;
  /** The moment behind `when`, for its tooltip; null when unknown. */
  at: string | null;
  /** The player's time is at least as fast as the server record (ties share it). */
  isRecord: boolean;
  /** The server record's preformatted time; null when the server has no measured record. */
  serverBestTime: string | null;
}

export interface RecentGameEntry {
  /**
   * Stable, untranslated finishing state: `WON` / `LIVE` / `VOID`, or an
   * English ordinal (`2ND`, `12TH`) for any other position. Kept as a machine
   * code so the SPA can style and localise it; `rank` carries the number.
   */
  place: string;
  /** Finishing position when the game produced one; null for WON/LIVE/VOID. */
  rank: number | null;
  id: number;
  server: string;
  outcome: string;
  /** Humanised server-side; null while the game is still running. */
  when: string | null;
  /** The moment behind `when`, for its tooltip; null when unknown. */
  at: string | null;
  points: number;
}

/** `GET /api/players/{name}`. Also drives the logged-in dashboard. */
export interface PlayerProfile {
  name: string;
  isRegistered: boolean;
  /** Under an active ban (permanent or running temp ban) - the legacy "(Banned)" marker. */
  isBanned: boolean;
  /** Uploaded avatar URL of a registered user; null → anonymous silhouette. */
  avatar: string | null;
  careerPoints: number;
  memberSince: string | null;
  seasonRank: number | null;
  level: number;
  xp: {
    current: number;
    next: number;
  };
  stats: PlayerStats;
  achievements: PlayerAchievementBadge[];
  /**
   * The linked account's legacy user-level badges (the retired `/user/{id}`
   * page); empty for a player with no account.
   */
  accountAchievements: PlayerAchievementBadge[];
  latestPoints: LatestPointsEntry[];
  seasonResults: SeasonResultEntry[];
  /** The player's fastest win per visible server, in server-list order. */
  personalBests: PersonalBestEntry[];
  recentGames: RecentGameEntry[];
}

/** `GET /api/players/{name}` - public profile (also the logged-in dashboard). */
export function usePlayer(name: string | undefined): UseQueryResult<PlayerProfile> {
  return useQuery({
    queryKey: queryKeys.players.detail(name ?? ''),
    enabled: name !== undefined && name.length > 0,
    queryFn: async () => {
      const res = await api.get<Envelope<PlayerProfile>>(
        `/players/${encodeURIComponent(name ?? '')}`,
      );
      return res.data;
    },
  });
}
